'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { IpInfoResponse } from '../services/client/ClientUserIpInfoService';
import { useClientUserIpInfoService } from '../services/client/ClientUserIpInfoService';
import type { AcceptedCurrency } from '@dr-pam/common-types/database/client';

type CurrencyContextValue = {
	currentCurrency: AcceptedCurrency;
	setCurrentCurrency: (currency: AcceptedCurrency) => void;
	location: IpInfoResponse | null;
	loading: boolean;
};

const COUNTRY_TO_CURRENCY: Record<string, AcceptedCurrency> = {
	US: 'USD',
	GB: 'GBP',
	AU: 'AUD',
	CA: 'CAD',
	NZ: 'NZD',
	// Eurozone countries
	AT: 'EUR',
	BE: 'EUR',
	CY: 'EUR',
	EE: 'EUR',
	FI: 'EUR',
	FR: 'EUR',
	DE: 'EUR',
	GR: 'EUR',
	IE: 'EUR',
	IT: 'EUR',
	LV: 'EUR',
	LT: 'EUR',
	LU: 'EUR',
	MT: 'EUR',
	NL: 'EUR',
	PT: 'EUR',
	SK: 'EUR',
	SI: 'EUR',
	ES: 'EUR',
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
	const [currency, setCurrency] = useState<AcceptedCurrency>('USD'); // USD as default
	const [location, setLocation] = useState<IpInfoResponse | null>(null);
	const [loading, setLoading] = useState(true);

	const userIpInfoService = useClientUserIpInfoService();

	useEffect(() => {
		// Check if currency is set in local storage and use if set.
		const savedCurrency = localStorage.getItem('currency');

		if (savedCurrency) {
			setCurrency(savedCurrency as AcceptedCurrency);
			setLoading(false);
			return;
		}

		// If no currency is set in local storage, detect the user's location and currency from their IP address.
		// Default to USD if location cannot be detected.
		userIpInfoService
			.getUserIpInfo()
			.then(({ response }) =>
				response.then((data) => {
					setLocation(data);
					const detectedCurrency = COUNTRY_TO_CURRENCY[data.country] ?? 'USD';
					setCurrency(detectedCurrency);
					localStorage.setItem('currency', detectedCurrency);
				}),
			)
			.catch((err) => console.error('Failed to detect location:', err))
			.finally(() => setLoading(false));
	}, [userIpInfoService]);

	const updateCurrency = (newCurrency: AcceptedCurrency) => {
		setCurrency(newCurrency);
		localStorage.setItem('currency', newCurrency.toString());
	};

	return (
		<CurrencyContext.Provider
			value={{ currentCurrency: currency, setCurrentCurrency: updateCurrency, location, loading }}
		>
			{children}
		</CurrencyContext.Provider>
	);
}

export function useCurrencyContext() {
	const context = useContext(CurrencyContext);
	if (!context) {
		throw new Error('useCurrencyContext must be used within CurrencyProvider');
	}
	return context;
}
